import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Play, Pause, RotateCcw, ArrowRight, Sparkles, Pin,
  CheckCircle2, MessageSquare, Target, TrendingUp, Mic
} from 'lucide-react';

// Bake the sample call data into the page so /demo works with zero backend calls.
// Keep in sync with /app/backend/sample_data.py — if you change one, change both.
const SAMPLE_TRANSCRIPT = [
  { speaker: 'rep', name: 'You', text: "Hi Mike, thanks for jumping on. To make this efficient, mind if I ask a few questions before we dive into the demo?" },
  { speaker: 'prospect', name: 'Mike Carter', text: "Sure, that works." },
  { speaker: 'rep', name: 'You', text: "First — what kicked off your search for a sales coaching tool right now?" },
  { speaker: 'prospect', name: 'Mike Carter', text: "Honestly, our new reps are taking 5-6 months to ramp. Two of our last three hires didn't hit quota in their first quarter. Leadership wants ramp time cut in half." },
  { speaker: 'rep', name: 'You', text: "That's a real problem. How many reps are you looking to ramp this year?" },
  { speaker: 'prospect', name: 'Mike Carter', text: "We're hiring 8 SDRs and 3 AEs over the next two quarters." },
  { speaker: 'rep', name: 'You', text: "Got it. Who else is involved in evaluating tooling for the sales team?" },
  { speaker: 'prospect', name: 'Mike Carter', text: "Me as VP Sales, our Head of Revenue Ops, and our CFO signs off on anything over $20k annually." },
  { speaker: 'rep', name: 'You', text: "Makes sense. Have you looked at any other solutions, or are we early in your process?" },
  { speaker: 'prospect', name: 'Mike Carter', text: "We demo'd Gong last month. Strong product but pricey, and frankly we don't need post-call analytics — we need help DURING the call. That's why PipeGuide caught my eye." },
  { speaker: 'rep', name: 'You', text: "That makes total sense — and that's exactly where PipeGuide is different. When were you hoping to have something live and helping your team?" },
  { speaker: 'prospect', name: 'Mike Carter', text: "Ideally by start of next quarter — that's when our new cohort starts." },
];

// Question pool — questions get suggested as the call progresses, demonstrating
// how the AI's recommendations evolve in real-time. Keyed to the transcript turn
// they unlock at, so the demo "reveals" insight as the conversation deepens.
const QUESTION_PROGRESSION = [
  { unlockAt: 2, question: "What kicked off your search for this right now?", category: 'Pain', priority: 'high' },
  { unlockAt: 4, question: "How many reps are you looking to ramp this year?", category: 'Need', priority: 'high' },
  { unlockAt: 6, question: "Who else is involved in evaluating tooling for the sales team?", category: 'Authority', priority: 'high' },
  { unlockAt: 8, question: "Have you evaluated other solutions in this space?", category: 'Competition', priority: 'medium' },
  { unlockAt: 10, question: "When were you hoping to have something live?", category: 'Timeline', priority: 'high' },
  { unlockAt: 12, question: "What's the budget range you're thinking about for this kind of solution?", category: 'Budget', priority: 'high' },
];

// Score progression — scores climb as qualification questions get answered.
// Mirrors what reps see during real calls (gentle ramp, not all-at-once).
const SCORE_TIMELINE = [
  { atTurn: 0,  Pain: 0,  Authority: 0,  Need: 0,  Timeline: 0,  Budget: 0,  Competition: 0 },
  { atTurn: 4,  Pain: 70, Authority: 0,  Need: 40, Timeline: 0,  Budget: 0,  Competition: 0 },
  { atTurn: 6,  Pain: 85, Authority: 0,  Need: 70, Timeline: 0,  Budget: 0,  Competition: 0 },
  { atTurn: 8,  Pain: 90, Authority: 85, Need: 75, Timeline: 0,  Budget: 50, Competition: 0 },
  { atTurn: 10, Pain: 90, Authority: 85, Need: 80, Timeline: 0,  Budget: 60, Competition: 90 },
  { atTurn: 12, Pain: 90, Authority: 85, Need: 80, Timeline: 75, Budget: 70, Competition: 90 },
];

const SCORE_CATEGORIES = ['Pain', 'Authority', 'Need', 'Timeline', 'Budget', 'Competition'];

function getScoresAtTurn(turn) {
  // Find the most recent snapshot whose `atTurn` is <= the current turn.
  let snap = SCORE_TIMELINE[0];
  for (const s of SCORE_TIMELINE) {
    if (s.atTurn <= turn) snap = s;
  }
  return snap;
}

function getOverallScore(scores) {
  const vals = SCORE_CATEGORIES.map(c => scores[c] || 0);
  return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
}

const PRIORITY_STYLE = {
  high:   { bg: '#FEE2E2', color: '#991B1B', label: 'High' },
  medium: { bg: '#FEF3C7', color: '#92400E', label: 'Medium' },
  low:    { bg: '#F4F4F5', color: '#52525B', label: 'Low' },
};

export default function DemoPage() {
  const [turn, setTurn] = useState(0);              // transcript index currently visible
  const [playing, setPlaying] = useState(false);
  const [pinnedQuestionIdx, setPinnedQuestionIdx] = useState(null);
  const timerRef = useRef(null);
  const transcriptScrollRef = useRef(null);

  // Auto-advance when playing
  useEffect(() => {
    if (!playing) return;
    if (turn >= SAMPLE_TRANSCRIPT.length) {
      setPlaying(false);
      return;
    }
    timerRef.current = setTimeout(() => setTurn(t => t + 1), 2800);
    return () => clearTimeout(timerRef.current);
  }, [turn, playing]);

  // Auto-scroll transcript to bottom as new turns appear
  useEffect(() => {
    if (transcriptScrollRef.current) {
      transcriptScrollRef.current.scrollTo({
        top: transcriptScrollRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [turn]);

  const reset = () => {
    setTurn(0);
    setPlaying(false);
    setPinnedQuestionIdx(null);
  };
  const toggle = () => {
    if (turn >= SAMPLE_TRANSCRIPT.length) reset();
    setPlaying(p => !p);
  };

  const visibleTranscript = SAMPLE_TRANSCRIPT.slice(0, turn);
  const scores = getScoresAtTurn(turn);
  const overall = getOverallScore(scores);
  const unlockedQuestions = QUESTION_PROGRESSION.filter(q => q.unlockAt <= turn);

  // Pinned question always appears first
  const orderedQuestions = (() => {
    if (pinnedQuestionIdx === null) return unlockedQuestions;
    const pin = unlockedQuestions[pinnedQuestionIdx];
    if (!pin) return unlockedQuestions;
    return [pin, ...unlockedQuestions.filter((_, i) => i !== pinnedQuestionIdx)];
  })();

  const progress = SAMPLE_TRANSCRIPT.length === 0 ? 0 : Math.round((turn / SAMPLE_TRANSCRIPT.length) * 100);
  const callComplete = turn >= SAMPLE_TRANSCRIPT.length;

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Top nav */}
      <header className="bg-brand-primary text-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-lg font-bold tracking-tight">PipeGuide</Link>
          <div className="flex items-center gap-3 text-sm">
            <Link to="/login" className="opacity-80 hover:opacity-100">Sign in</Link>
            <Link
              to="/login"
              className="bg-brand-signature hover:bg-brand-signature-hover text-brand-signature-strong font-semibold px-4 py-1.5 rounded-md"
              data-testid="demo-nav-signup"
            >
              Try it free
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 pt-10 pb-6">
        <div className="flex items-center gap-2 text-brand-primary mb-3">
          <Sparkles className="w-4 h-4" />
          <span className="text-xs font-bold uppercase tracking-[0.18em]">Interactive Demo</span>
        </div>
        <h1 className="text-3xl md:text-5xl font-bold text-zinc-900 max-w-3xl leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
          See PipeGuide score a live call —{' '}
          <span className="text-brand-primary">no signup required</span>
        </h1>
        <p className="text-zinc-600 mt-3 max-w-2xl">
          This is exactly what your reps see during a real qualification call. Press <strong>Play</strong> to
          simulate Mike from Acme Co talking to your rep. Watch the AI score, suggest questions, and refresh in real-time.
        </p>
      </section>

      {/* Player controls */}
      <section className="max-w-7xl mx-auto px-6 mb-4">
        <div className="bg-white border border-zinc-200 rounded-xl p-4 flex flex-wrap items-center gap-3">
          <button
            onClick={toggle}
            className="flex items-center gap-2 bg-brand-primary hover:bg-brand-hover text-white px-5 py-2.5 rounded-md font-semibold text-sm"
            data-testid="demo-play-btn"
          >
            {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            {callComplete ? 'Replay' : playing ? 'Pause' : 'Play'}
          </button>
          <button
            onClick={reset}
            className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900 px-3 py-2 rounded-md text-sm"
            data-testid="demo-reset-btn"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
          <div className="flex-1 min-w-[200px]">
            <div className="flex justify-between text-xs text-zinc-500 mb-1">
              <span>Turn {turn} / {SAMPLE_TRANSCRIPT.length}</span>
              <span>{progress}%</span>
            </div>
            <div className="h-2 bg-zinc-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-brand-primary transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Main 3-column layout: scores | transcript | questions */}
      <section className="max-w-7xl mx-auto px-6 pb-12 grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Left: Qualification scores */}
        <aside className="lg:col-span-3 space-y-3">
          <div className="bg-white border border-zinc-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <Target className="w-4 h-4 text-brand-primary" />
              <h3 className="font-bold text-sm uppercase tracking-wide text-zinc-700">Qualification</h3>
            </div>
            <div className="flex items-center justify-center my-4" data-testid="demo-overall-score">
              <div className="relative w-32 h-32">
                <svg className="w-full h-full -rotate-90">
                  <circle cx="64" cy="64" r="56" stroke="#E4E4E7" strokeWidth="8" fill="none" />
                  <circle
                    cx="64" cy="64" r="56"
                    stroke="#0E4E5A"
                    strokeWidth="8"
                    fill="none"
                    strokeDasharray={`${(overall / 100) * 351.86} 351.86`}
                    strokeLinecap="round"
                    className="transition-all duration-700"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div className="text-3xl font-bold text-zinc-900">{overall}</div>
                  <div className="text-[10px] uppercase tracking-wide text-zinc-500">Overall</div>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              {SCORE_CATEGORIES.map(cat => {
                const val = scores[cat] || 0;
                return (
                  <div key={cat} data-testid={`demo-score-${cat.toLowerCase()}`}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-zinc-700 font-medium">{cat}</span>
                      <span className="text-zinc-900 font-bold tabular-nums">{val}</span>
                    </div>
                    <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-brand-primary transition-all duration-700"
                        style={{ width: `${val}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </aside>

        {/* Middle: Live transcript */}
        <main className="lg:col-span-5">
          <div className="bg-white border border-zinc-200 rounded-xl flex flex-col" style={{ height: '560px' }}>
            <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Mic className={`w-4 h-4 ${playing ? 'text-red-500 animate-pulse' : 'text-zinc-400'}`} />
                <h3 className="font-bold text-sm">Live transcript</h3>
              </div>
              {playing && (
                <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-red-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                  Recording
                </div>
              )}
            </div>
            <div ref={transcriptScrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="demo-transcript">
              {visibleTranscript.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center text-zinc-400">
                  <MessageSquare className="w-12 h-12 mb-3" />
                  <p className="text-sm">Press <strong className="text-brand-primary">Play</strong> to start the simulated call</p>
                </div>
              ) : (
                visibleTranscript.map((entry, i) => (
                  <div
                    key={i}
                    className={`flex flex-col ${entry.speaker === 'rep' ? 'items-end' : 'items-start'}`}
                    data-testid={`demo-transcript-entry-${i}`}
                  >
                    <div className="text-[10px] font-bold uppercase tracking-wide text-zinc-500 mb-1 px-1">
                      {entry.name}
                    </div>
                    <div
                      className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                        entry.speaker === 'rep'
                          ? 'bg-brand-primary text-white rounded-tr-sm'
                          : 'bg-zinc-100 text-zinc-900 rounded-tl-sm'
                      }`}
                    >
                      {entry.text}
                    </div>
                  </div>
                ))
              )}
              {callComplete && (
                <div className="border-t border-zinc-100 pt-4 mt-4 text-center" data-testid="demo-call-complete">
                  <CheckCircle2 className="w-8 h-8 mx-auto text-green-600 mb-1" />
                  <div className="font-semibold text-sm text-zinc-900">Call complete</div>
                  <div className="text-xs text-zinc-500 mt-0.5">PipeGuide would now generate a PDF summary and push to your CRM</div>
                </div>
              )}
            </div>
          </div>
        </main>

        {/* Right: AI suggested questions */}
        <aside className="lg:col-span-4">
          <div className="bg-white border border-zinc-200 rounded-xl flex flex-col" style={{ height: '560px' }}>
            <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-brand-primary" />
                <h3 className="font-bold text-sm">Ask next</h3>
              </div>
              <div className="text-[10px] font-medium text-zinc-500">
                Refreshes every 15s
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="demo-questions">
              {orderedQuestions.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center text-zinc-400 px-4">
                  <Sparkles className="w-10 h-10 mb-3 opacity-50" />
                  <p className="text-sm">Questions will appear here based on the conversation</p>
                </div>
              ) : (
                orderedQuestions.map((q, i) => {
                  const originalIdx = unlockedQuestions.indexOf(q);
                  const isPinned = originalIdx === pinnedQuestionIdx;
                  const style = PRIORITY_STYLE[q.priority] || PRIORITY_STYLE.low;
                  return (
                    <div
                      key={`${q.unlockAt}-${i}`}
                      className={`border rounded-md p-3 transition-all ${
                        isPinned
                          ? 'bg-brand-signature-muted border-brand-signature shadow-sm'
                          : 'bg-white border-zinc-200 hover:border-zinc-300'
                      }`}
                      data-testid={`demo-question-${i}`}
                    >
                      {isPinned && (
                        <div className="flex items-center gap-1.5 mb-2">
                          <Pin className="w-3 h-3 text-brand-signature-strong" />
                          <span className="text-[9px] font-bold uppercase tracking-[0.15em] text-brand-signature-strong">
                            Pinned
                          </span>
                        </div>
                      )}
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <span
                          className="text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded"
                          style={{ background: '#E6F3F5', color: '#0E4E5A' }}
                        >
                          {q.category}
                        </span>
                        <span
                          className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded"
                          style={{ background: style.bg, color: style.color }}
                        >
                          {style.label}
                        </span>
                      </div>
                      <p className="text-sm text-zinc-900 leading-snug">{q.question}</p>
                      <button
                        onClick={() => setPinnedQuestionIdx(isPinned ? null : originalIdx)}
                        className={`mt-2 text-[10px] font-semibold inline-flex items-center gap-1 px-2 py-0.5 rounded ${
                          isPinned
                            ? 'bg-brand-signature-strong text-white'
                            : 'border border-zinc-200 text-zinc-600 hover:border-brand-signature hover:text-brand-signature-strong'
                        }`}
                        data-testid={`demo-pin-btn-${i}`}
                      >
                        <Pin className="w-2.5 h-2.5" />
                        {isPinned ? 'Unpin' : 'Pin to top'}
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </aside>
      </section>

      {/* Conversion CTA — high-signal Guidance Gold */}
      <section className="bg-brand-primary text-white py-14 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 text-brand-accent mb-3">
            <TrendingUp className="w-4 h-4" />
            <span className="text-xs font-bold uppercase tracking-[0.18em]">Ready to try it on real calls?</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
            Your reps deserve a coach on every call
          </h2>
          <p className="text-white/80 mb-6 max-w-xl mx-auto">
            Add PipeGuide to your next discovery call in 90 seconds. No credit card required to start —
            we'll seed your account with a sample call so you can poke around first.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to="/login"
              className="bg-brand-signature hover:bg-brand-signature-hover text-brand-signature-strong font-bold px-6 py-3 rounded-md inline-flex items-center gap-2"
              data-testid="demo-cta-signup"
            >
              Start free
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="mailto:sales@pipeguide.tech"
              className="border border-white/30 hover:bg-white/10 text-white font-semibold px-6 py-3 rounded-md"
            >
              Talk to sales
            </a>
          </div>
          <p className="text-xs text-white/60 mt-4">
            Built for teams running real qualification calls on Google Meet, Microsoft Teams, and Zoom.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-zinc-900 text-zinc-500 text-xs py-6 px-6">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-3">
          <div>© 2026 PipeGuide. Built for sales teams.</div>
          <div className="flex gap-4">
            <Link to="/changelog" className="hover:text-white">What's new</Link>
            <Link to="/status" className="hover:text-white">Status</Link>
            <Link to="/sla" className="hover:text-white">SLA</Link>
            <Link to="/sub-processors" className="hover:text-white">Sub-processors</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
